import type { InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import { clsx } from 'clsx';
import styles from './Input.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  /**
   * Input label
   */
  label?: string;

  /**
   * Input size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Success state
   * @default false
   */
  success?: boolean;

  /**
   * Helper text displayed below input
   */
  helperText?: string;

  /**
   * Prefix content (icon or text)
   */
  prefix?: ReactNode;

  /**
   * Suffix content (icon or text)
   */
  suffix?: ReactNode;

  /**
   * Full width input
   * @default true
   */
  fullWidth?: boolean;

  /**
   * Required field indicator
   * @default false
   */
  required?: boolean;

  /**
   * Additional CSS classes for wrapper
   */
  wrapperClassName?: string;

  /**
   * Additional CSS classes for input element
   */
  className?: string;
}

/**
 * Input Component
 *
 * A flexible text input component with label, error states, and prefix/suffix support.
 * Follows WCAG 2.1 AA accessibility standards.
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="Enter your email"
 *   error="Invalid email address"
 *   required
 * />
 * ```
 */
export const Input = ({
  label,
  size = 'md',
  error,
  success = false,
  helperText,
  prefix,
  suffix,
  fullWidth = true,
  required = false,
  wrapperClassName,
  className,
  id,
  ...props
}: InputProps) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const hasError = Boolean(error);
  const showHelperText = !hasError && helperText;
  const isReadOnly = props.readOnly;

  // Add lock icon for read-only fields if no suffix is provided
  const effectiveSuffix = isReadOnly && !suffix ? (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M11.5 7H11V5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5V7H4.5C3.67157 7 3 7.67157 3 8.5V12.5C3 13.3284 3.67157 14 4.5 14H11.5C12.3284 14 13 13.3284 13 12.5V8.5C13 7.67157 12.3284 7 11.5 7ZM6.5 5C6.5 4.17157 7.17157 3.5 8 3.5C8.82843 3.5 9.5 4.17157 9.5 5V7H6.5V5Z" fill="currentColor" opacity="0.5"/>
    </svg>
  ) : suffix;

  return (
    <div
      className={clsx(
        styles.inputWrapper,
        fullWidth && styles.fullWidth,
        hasError && styles.error,
        success && styles.success,
        wrapperClassName
      )}
    >
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && (
            <span className={styles.required} aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      <div
        className={clsx(
          styles.inputContainer,
          prefix && styles.hasPrefix,
          effectiveSuffix && styles.hasSuffix
        )}
      >
        {prefix && <span className={styles.prefix}>{prefix}</span>}

        <input
          id={inputId}
          className={clsx(styles.input, styles[`size-${size}`], className)}
          aria-invalid={hasError}
          aria-describedby={
            hasError
              ? `${inputId}-error`
              : showHelperText
              ? `${inputId}-helper`
              : undefined
          }
          required={required}
          {...props}
        />

        {effectiveSuffix && <span className={styles.suffix}>{effectiveSuffix}</span>}
      </div>

      {hasError && (
        <span id={`${inputId}-error`} className={styles.errorMessage} role="alert">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8C1.5 11.59 4.41 14.5 8 14.5C11.59 14.5 14.5 11.59 14.5 8C14.5 4.41 11.59 1.5 8 1.5ZM8 13C5.24 13 3 10.76 3 8C3 5.24 5.24 3 8 3C10.76 3 13 5.24 13 8C13 10.76 10.76 13 8 13ZM7.25 5.5H8.75V9H7.25V5.5ZM7.25 10H8.75V11.5H7.25V10Z"
              fill="currentColor"
            />
          </svg>
          {error}
        </span>
      )}

      {showHelperText && (
        <span id={`${inputId}-helper`} className={styles.helperText}>
          {helperText}
        </span>
      )}
    </div>
  );
};
